import React, {useEffect, useRef, useState} from 'react';
import Card from "../../../components/card";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import TemplateDefault from "../../../components/template-default";
import SelectComponent from "../../../components/select-component";
import InputDateTime from "../../../components/input-datetime";
import {useQuery} from "@tanstack/react-query";
import projectService from "../../../services/project-service";
import {FaList, FaPrint} from "react-icons/fa";
import Button from "../../../components/button";
import actionService from "../../../services/action-service";
import moment from "moment/moment";
import reportService from "../../../services/report-service";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import {print} from "../components/print";
import {document} from "postcss";
import general from "../../../utils/general";
import RequestRender from "../components/request-render";

const BASENAME = process.env.PUBLIC_URL;

const initialFilters = {
    init: moment().startOf('month').toDate(),
    end: moment().endOf('month').toDate(),
    project: "",
    actions: []
}

const HardwareAttendanceReport = () => {
    const [data, setData] = useState([]);
    const [filtersStr, setFiltersStr] = useState("");
    const [filters, setFilters] = useState(initialFilters);
    const [pinnedBottomValues, setPinnedBottomValues] = useState({ requester: "TOTAL DE CHAMADOS: 0" });
    const onCellClicked = (cell) => window.open(`${BASENAME}/hardware-attendances/form/${cell.data.id}`);
    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = (value, name) => setFilters(prev => ({...prev, [name]: value}));

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const {data: projects} = useQuery({
        queryFn: () => projectService.options().then(({data}) => data),
        queryKey: ["projects"]
    });

    const {data: actions} = useQuery({
        queryFn: () => actionService.options().then(({data}) => data),
        queryKey: ["actions"]
    });

    const [columnDefs] = useState([
        {field: "id", headerName: "ID", width: 50},
        {field: "date_initial", headerName: "Abertura", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "date_final", headerName: "Encerramento", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "status", headerName: "Status"},
        {field: "place", headerName: "Local", wrapText: true, autoHeight: true, flex: 1, minWidth: 250},
        {field: "problem", headerName: "Problema", wrapText: true, autoHeight: true, flex: 1, minWidth: 250},
        {field: "actions", headerName: "Ações"},
        {field: "requester", headerName: "Solicitante",
            cellRenderer: RequestRender,
            cellRendererParams: {
                data: data,
                url: "/hardware-attendances/form/"
            }},
    ]);

    const gridRef = useRef();

    const formatDate = date => {
        if (date) return date.toLocaleDateString() + " " + date.toLocaleTimeString();

        return "";
    }

    useEffect(() => {
        setPinnedBottomValues({requester: `TOTAL DE CHAMADOS: ${data.length}`})
    }, [data]);

    const apply = () => {
        const init = formatDate(filters.init);
        const end = formatDate(filters.end);
        const formData = new FormData();

        formData.append("init", init);
        formData.append("end", end);
        formData.append("project", filters.project?.value ?? "");
        formData.append("actions", filters.actions.length > 0 ? JSON.stringify(filters.actions) : []);

        const filtersStr = `Início: ${init} - Fim: ${end}`;
        setFiltersStr(filtersStr);

        reportService.hardwareAttendance(formData)
            .then(({data}) => setData(data))
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível prosseguir.");
            });
    }

    const handlePrint = () => {
        if (gridRef.current) {
            const title = `RELATÓRIO DE CHAMADOS - ${filters.project.label ?? "N/A"}`;
            const footer = `<tr><td colspan='7'></td><td>${pinnedBottomValues.requester}</td></tr>`
            print(gridRef, columnDefs, title, filtersStr, footer);
        }
    }

    return (
        <TemplateDefault title="Relatório de chamados">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-init"} name={"init"} label={"Inicio"} selected={filters.init}
                                       onChange={(value) => handleInput(value, "init")} showTime/>
                    </div>
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-end"} name={"end"} label={"Fim"} selected={filters.end}
                                       onChange={(value) => handleInput(value, "end")} showTime/>
                    </div>

                    <div className="col-span-2 text-gray-900">
                        <SelectComponent
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="projects"
                        />
                    </div>

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="actions"
                            label="Ações"
                            options={actions ?? []}
                            value={filters.actions}
                            onChange={(value) => handleSelect("actions", value)}
                            name="actions"
                        />
                    </div>
                    <Button col={"col-span-1"} onClick={apply}>
                        <FaList/> <span className={"ml-1"}>Aplicar</span>
                    </Button>
                    <Button col={"col-span-1"} onClick={handlePrint}>
                        <FaPrint/>
                    </Button>

                </div>
                <div className={"ag-theme-quartz grid mt-4 report-table"} style={{height: "calc(100vh - 159px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={data}
                        columnDefs={columnDefs}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        domLayout='autoHeight'
                        pinnedBottomRowData={[pinnedBottomValues]}
                        getRowStyle={(params) => {
                            if (params.node.rowPinned) {
                                return { pointerEvents: "none", fontWeight: "700"};
                            }
                        }}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default HardwareAttendanceReport;